import { usePostHog } from "posthog-js/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { callWithAuth, getUserInfo } from "@/api/Api";
import {
  FLIGHT_SCHOOL_VERSION,
  getFlightSchoolActivityStepEventName,
  getFlightSchoolMilestones,
  getFlightSchoolModuleEventProps,
  getFlightSchoolModuleEventName,
} from "./flightSchoolEventMetadata";

/**
 * Flight School analytics — web parity for iOS
 * (client-native/src/hooks/posthog/useTrackFlightSchoolEvents.tsx). Emits the
 * full module/step/milestone/graduation funnel via PostHog. Web divergences:
 * `platform: "web"`, and the user's placement level is read from the cached
 * userInfo query instead of iOS's globalStore.
 */

// ============================================
// Pattern name to event suffix mapping
// ============================================

const PATTERN_EVENT_SUFFIX: Record<string, string> = {
  CION: "cion",
  ANTE: "ante",
  DAD: "dad",
  MENTE: "mente",
};

// ============================================
// Session timer singleton
// ============================================

let flightSchoolSessionStartTime: number | null = null;

function startSessionTimer() {
  if (!flightSchoolSessionStartTime) {
    flightSchoolSessionStartTime = Date.now();
  }
}

function getSessionDurationSeconds(): number {
  if (!flightSchoolSessionStartTime) return 0;
  return Math.round((Date.now() - flightSchoolSessionStartTime) / 1000);
}

function resetSessionTimer() {
  flightSchoolSessionStartTime = null;
}

export function useTrackFlightSchoolEvents() {
  const posthog = usePostHog();
  const firedMilestones = useRef<Set<string>>(new Set());

  // Placement level for `user_level` (iOS reads globalStore.userPlacementResult).
  // Shared cache key with the rest of the app — no extra fetch.
  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });

  /** Base properties included in every event. */
  const baseProps = useCallback(() => {
    return {
      user_level: userInfoQuery.data?.placementResult || "unknown",
      platform: "web" as const,
    };
  }, [userInfoQuery.data]);

  // ------------------------------------------
  // Entry event
  // ------------------------------------------

  const trackFlightSchoolStarted = useCallback(
    (moduleId: string, completedModulesCount: number) => {
      startSessionTimer();
      posthog?.capture("flight_school_started", {
        ...baseProps(),
        ...getFlightSchoolModuleEventProps(moduleId),
        module_id: moduleId,
        completed_modules_count: completedModulesCount,
        is_first_visit: completedModulesCount === 0,
      });
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Module started
  // ------------------------------------------

  const trackModuleStarted = useCallback(
    (moduleId: string) => {
      startSessionTimer();
      posthog?.capture(getFlightSchoolModuleEventName(moduleId, "started"), {
        ...baseProps(),
        ...getFlightSchoolModuleEventProps(moduleId),
      });
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Step completed + milestone detection
  // ------------------------------------------

  const trackStepCompleted = useCallback(
    (
      moduleId: string,
      activityIndex: number,
      activityType: string,
      totalActivities: number,
    ) => {
      const stepNumber = activityIndex + 1; // 1-based for event name
      const progressPercent =
        Math.round((stepNumber / totalActivities) * 100 * 100) / 100;

      // Fire the step event
      posthog?.capture(
        getFlightSchoolActivityStepEventName(moduleId, activityIndex),
        {
          ...baseProps(),
          ...getFlightSchoolModuleEventProps(moduleId),
          activity_type: activityType,
          activity_index: activityIndex,
          progress_percent: progressPercent,
        },
      );

      const milestones = getFlightSchoolMilestones(moduleId);
      if (milestones) {
        const milestoneChecks: [number, string][] = [
          [milestones.one_third, "one_third_complete"],
          [milestones.halfway, "halfway_complete"],
          [milestones.two_thirds, "two_thirds_complete"],
        ];

        for (const [threshold, eventSuffix] of milestoneChecks) {
          const milestoneKey = `${moduleId}_${eventSuffix}`;
          if (
            stepNumber >= threshold &&
            !firedMilestones.current.has(milestoneKey)
          ) {
            firedMilestones.current.add(milestoneKey);
            posthog?.capture(
              getFlightSchoolModuleEventName(moduleId, eventSuffix),
              {
                ...baseProps(),
                ...getFlightSchoolModuleEventProps(moduleId),
              },
            );
          }
        }
      }
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Activity attempt (correct/incorrect)
  // ------------------------------------------

  const trackActivityAttempt = useCallback(
    (
      moduleId: string,
      activityType: string,
      activityIndex: number,
      isCorrect: boolean,
      attemptNumber: number,
    ) => {
      posthog?.capture(
        getFlightSchoolModuleEventName(moduleId, "activity_attempt"),
        {
          ...baseProps(),
          ...getFlightSchoolModuleEventProps(moduleId),
          activity_type: activityType,
          activity_index: activityIndex,
          is_correct: isCorrect,
          attempt_number: attemptNumber,
        },
      );
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Pattern unlocked
  // ------------------------------------------

  const trackPatternUnlocked = useCallback(
    (pattern: string) => {
      const suffix = PATTERN_EVENT_SUFFIX[pattern];
      if (!suffix) return;
      posthog?.capture(`flight_school_pattern_unlocked_${suffix}`, {
        ...baseProps(),
      });
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Flashcard completed
  // ------------------------------------------

  const trackFlashcardCompleted = useCallback(
    (
      moduleId: string,
      knownCount: number,
      learningCount: number,
      totalWords: number,
    ) => {
      posthog?.capture(
        getFlightSchoolModuleEventName(moduleId, "flashcard_completed"),
        {
          ...baseProps(),
          ...getFlightSchoolModuleEventProps(moduleId),
          known_count: knownCount,
          learning_count: learningCount,
          total_words: totalWords,
        },
      );
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Module completed
  // ------------------------------------------

  const trackModuleCompleted = useCallback(
    (moduleId: string, knownWordsCount: number, learningWordsCount: number) => {
      posthog?.capture(getFlightSchoolModuleEventName(moduleId, "completed"), {
        ...baseProps(),
        ...getFlightSchoolModuleEventProps(moduleId),
        session_duration_seconds: getSessionDurationSeconds(),
        known_words_count: knownWordsCount,
        learning_words_count: learningWordsCount,
      });
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Graduated
  // ------------------------------------------

  const trackFlightSchoolGraduated = useCallback(
    (totalModulesCompleted: number) => {
      posthog?.capture("flight_school_graduated", {
        ...baseProps(),
        flight_school_version: FLIGHT_SCHOOL_VERSION,
        total_modules_completed: totalModulesCompleted,
      });
      resetSessionTimer();
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Abandoned (user exits mid-module)
  // ------------------------------------------

  const trackAbandoned = useCallback(
    (
      moduleId: string,
      activityIndex: number,
      activityType: string,
      progressPercent: number,
    ) => {
      posthog?.capture(getFlightSchoolModuleEventName(moduleId, "abandoned"), {
        ...baseProps(),
        ...getFlightSchoolModuleEventProps(moduleId),
        activity_index: activityIndex,
        activity_type: activityType,
        progress_percent: progressPercent,
        session_duration_seconds: getSessionDurationSeconds(),
      });
      resetSessionTimer();
    },
    [posthog, baseProps],
  );

  // ------------------------------------------
  // Reset milestone tracking (call when starting a new module)
  // ------------------------------------------

  const resetMilestones = useCallback(() => {
    firedMilestones.current.clear();
  }, []);

  return {
    trackFlightSchoolStarted,
    trackModuleStarted,
    trackStepCompleted,
    trackActivityAttempt,
    trackPatternUnlocked,
    trackFlashcardCompleted,
    trackModuleCompleted,
    trackFlightSchoolGraduated,
    trackAbandoned,
    resetMilestones,
  };
}
